import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Tag } from 'antd';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import { dataGridTextSorter } from '../../../../common/DataGrid/sorters/DataGridSorters';
import DataGrid from '../../../../common/DataGrid/DataGrid';

const ErasmusSubjectsTable = ({
  erasmusSubjects = [],
  pivot = [],
  showMapsColumn = false,
  onDelete,
  onDetailClick,
  onPreviewClick,
}) => {
  const { t } = useTranslation();

  const NameFilterProps = useDataGridTextFilter('name');
  const CodeFilterProps = useDataGridTextFilter('local_subject_code');
  const LocalNameFilterProps = useDataGridTextFilter('local_subject_name');
  const UniversityFilterProps = useDataGridTextFilter('university');

  // Pomocná funkcia – vezmi prvý local subject (ak existuje) a vráť {code,name}
  const pickLocalSubject = (maybeArray) => {
    const arr = Array.isArray(maybeArray) ? maybeArray : [];
    if (!arr.length) return { code: '', name: '' };
    const first = arr[0] || {};
    return {
      code: first?.code ?? '',
      name: first?.name ?? '',
    };
  };

  // --- 1) Transformácia pivotu
  const fromPivot = Array.isArray(pivot)
    ? pivot.map((rec) => {
        const codeFromObj = rec?.local_subject?.code ?? '';
        const nameFromObj = rec?.local_subject?.name ?? '';
        const fromArr = pickLocalSubject(rec?.local_subjects);
        const code = codeFromObj || fromArr.code || '';
        const lname = nameFromObj || fromArr.name || '';

        const hasFriRef =
          !!code ||
          !!lname ||
          !!rec?.local_subject_id ||
          (Array.isArray(rec?.local_subjects) && rec.local_subjects.length > 0);

        return {
          id:
            rec.id ??
            `${rec.erasmus_subject_id}-${rec.local_subject_id ?? 'none'}`,
          name: rec?.erasmus_subject?.name ?? '',
          local_subject_code: code,
          local_subject_name: lname,
          faculty: hasFriRef ? 'FRI' : 'UNIZA',
        };
      })
    : [];

  // --- 2) Transformácia subjects
  const fromSubjectsRaw = Array.isArray(erasmusSubjects)
    ? erasmusSubjects.map((rec) => {
        const { code, name } = pickLocalSubject(rec?.local_subjects);
        const hasFriRef =
          (Array.isArray(rec?.local_subjects) &&
            rec.local_subjects.length > 0) ||
          !!code ||
          !!name;

        return {
          id: rec.id,
          name: rec.name ?? '',
          university: rec?.university?.name ?? '',
          local_subject_code: code,
          local_subject_name: name,
          faculty: hasFriRef ? 'FRI' : 'UNIZA',
        };
      })
    : [];

  // --- 3) DEDUP: ak predmet existuje v pivote, neukazuj jeho „čistý“ riadok
  const pivotSubjectIds = new Set(
    (Array.isArray(pivot) ? pivot : [])
      .map((r) => r?.erasmus_subject_id)
      .filter((v) => v != null),
  );
  const fromSubjects = fromSubjectsRaw.filter(
    (s) => !pivotSubjectIds.has(s.id),
  );

  // --- 4) Finálne dáta
  const data = [...fromPivot, ...fromSubjects];

  // Zobraz FRI stĺpce, ak aspoň jeden riadok má FRI kód/názov
  const showFriColumns = data.some(
    (r) =>
      (r.local_subject_code && r.local_subject_code !== '') ||
      (r.local_subject_name && r.local_subject_name !== ''),
  );

  const columns = useMemo(() => {
    // RowSpan pre názov –  iba pre pivot
    const rowSpanMap = {};
    if (fromPivot.length > 0) {
      const nameCounts = {};
      fromPivot.forEach((item) => {
        const key = item.name || '';
        nameCounts[key] = (nameCounts[key] || 0) + 1;
      });
      let idx = 0;
      Object.values(nameCounts).forEach((count) => {
        for (let i = 0; i < count; i++) {
          rowSpanMap[idx] = i === 0 ? count : 0;
          idx++;
        }
      });
    }

    const cols = [
      // Fakulta / Tag
      {
        title: t('Fakulta'),
        dataIndex: 'faculty',
        width: 120,
        filters: [
          { text: 'FRI', value: 'FRI' },
          { text: 'UNIZA', value: 'UNIZA' },
        ],
        onFilter: (value, record) => record.faculty === value,
        sorter: dataGridTextSorter('faculty'),
        render: (val) =>
          val === 'FRI' ? <Tag color="gold">FRI</Tag> : <Tag>UNIZA</Tag>,
      },
      // Názov predmetu
      {
        title: t('Názov'),
        dataIndex: 'name',
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        defaultSortOrder: 'ascend',
        onCell: (_, index) => ({
          rowSpan: fromPivot.length ? (rowSpanMap[index] ?? 1) : 1,
        }),
        ...NameFilterProps,
      },
      {
        title: t('Kód predmetu FRI'),
        dataIndex: 'local_subject_code',
        ellipsis: true,
        sorter: dataGridTextSorter('local_subject_code'),
        ...CodeFilterProps,
      },
      {
        title: t('Názov predmetu FRI'),
        dataIndex: 'local_subject_name',
        ellipsis: true,
        sorter: dataGridTextSorter('local_subject_name'),
        ...LocalNameFilterProps,
      },
    ];

    if (!showMapsColumn) {
      cols.push({
        title: t('Univerzita'),
        dataIndex: 'university',
        ellipsis: true,
        sorter: dataGridTextSorter('university'),
        ...UniversityFilterProps,
      });
    }

    return cols;
  }, [
    NameFilterProps,
    CodeFilterProps,
    LocalNameFilterProps,
    UniversityFilterProps,
    fromPivot.length,
    showMapsColumn,
    t,
  ]);

  return (
    <DataGrid
      rowKey={(row) => row.id}
      columns={columns}
      dataSource={data}
      permissionsBasic={'erasmus-subjects'}
      onEditClick={onDetailClick}
      onRowClick={onDetailClick}
      onDelete={onDelete}
      onPreviewClick={onPreviewClick}
      rowClickOpensPreview
    />
  );
};

ErasmusSubjectsTable.propTypes = {
  erasmusSubjects: PropTypes.array,
  pivot: PropTypes.array,
  showMapsColumn: PropTypes.bool,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onPreviewClick: PropTypes.func,
};

export default ErasmusSubjectsTable;
